/* eslint-disable linebreak-style */
import { ApplicationInsights, IExtendedConfiguration, IXHROverride } from '@microsoft/1ds-analytics-js';
import fetch from 'node-fetch';
import { clone } from 'underscore';
import { Buffer } from 'buffer';
import { ILogger, Data } from './Context';

export enum LogLevel {
  Verbose = 3,
  Info = 2,
  Warn = 1,
  Error = 0,
}

export type AriaLoggerOptions = {
  instrumentationKey: string,
  applicationName: string,
  appendedData: Data,
};

export class AriaLogger implements ILogger {
  private readonly analytics: ApplicationInsights;

  private readonly instrumentationKey: string;

  private readonly applicationName: string;

  private readonly appendedData: Data;

  constructor(options: AriaLoggerOptions) {
    this.analytics = new ApplicationInsights();
    this.applicationName = options.applicationName;
    this.appendedData = options.appendedData;
    this.instrumentationKey = options.instrumentationKey;

    const customHttpXHROverride: IXHROverride = {
      sendPOST: (payload, oncomplete) => {
        fetch(payload.urlString, {
          method: 'POST',
          headers: {
            ...payload.headers ?? {},
            'Content-Type': 'application/json',
            'Content-Length': Buffer.byteLength(payload.data).toString(),
          },
          body: payload.data as any,
        }).then((res) => res.text().then((text) => {
          const headers: { [headerName: string]: string } = {};
          res.headers.forEach((value, key) => {
            headers[key] = value;
          });

          oncomplete(res.status, headers, text);
        }));
      },
    };
    const config: IExtendedConfiguration = {
      instrumentationKey: options.instrumentationKey,
      channelConfiguration: {
        httpXHROverride: customHttpXHROverride,
      },
      propertyConfiguration: {
        sessionAsGuid: true,
      },
    };

    this.analytics.initialize(config, []);
  }

  logVerbose(message: string, additionalData?: Data): void {
    this.log(message, LogLevel.Verbose, additionalData);
  }

  logInfo(message: string, additionalData?: Data): void {
    this.log(message, LogLevel.Info, additionalData);
  }

  logWarning(message: string, additionalData?: Data): void {
    this.log(message, LogLevel.Warn, additionalData);
  }

  logError(message: string, additionalData?: Data): void {
    this.log(message, LogLevel.Error, additionalData);
  }

  clone(): AriaLogger {
    return new AriaLogger({
      instrumentationKey: this.instrumentationKey,
      applicationName: this.applicationName,
      appendedData: clone(this.appendedData),
    });
  }

  setAppendedData(key: string, value: string): void {
    this.appendedData[key] = value;
  }

  private log(message: string, level: LogLevel, additionalData?: Data) {
    let levelString: string;

    switch (level) {
      case LogLevel.Verbose:
        levelString = 'Verbose';
        break;
      case LogLevel.Info:
        levelString = 'Info';
        break;
      case LogLevel.Warn:
        levelString = 'Warn';
        break;
      case LogLevel.Error:
        levelString = 'Error';
        break;
      default:
        levelString = 'Info';
        break;
    }

    this.analytics.track({
      name: 'customEvent',
      data: {
        Content: this.removeCredentials(message),
        Type: levelString,
        ApplicationName: this.applicationName,
        ...this.appendedData,
        ...additionalData ?? {},
      },
    });
  }

  // For logs sent out, we must filter any credentials
  // Console logs are not a concern
  private removeCredentials(content: string) {
    // Remove authorization header with regex
    const regex = /Authorization:[^\n]*/g;

    const newContent = content.replace(regex, 'Authorization: ********');

    // Remove display name with replace id
    if (this.appendedData.AccountDisplayName) {
      return newContent.replace(this.appendedData.AccountDisplayName, this.appendedData.AccountDisplayNameReplaceId);
    }

    return newContent;
  }
}
